/**
 * trialStatus — pure derivation for the user's trial state.
 *
 * Derives everything the trial UI needs from users/{uid}.trial (written by
 * redeemAccessCode / deductTrialCredits / enforceTrialExpiry): days left, a
 * friendly end date, remaining credit, and whether the trial is expiring soon
 * or already over. No Firebase imports — unit-testable without the SDK; the
 * React hook lives in useTrialStatus.ts.
 *
 * Trial terms recap (see functions/src/accessCodes.ts TRIAL_TIERS):
 *   Tier A: $1,000 credit, 4 weeks.  Tier B: $300 credit, 2 weeks.
 * Trial accounts can never top up credits — at (or near) expiry the user is
 * directed to the subscription page to hire their prest0n (self-serve since
 * task #59; no personal contact email is ever printed — owner rule 2026-08-16).
 */

import { expiryToMillis, TrialExpiryValue } from '../components/GlobalChatSystem/entitlementBypass';

/** Days-left threshold at which we treat the trial as "expiring soon". */
export const TRIAL_EXPIRING_SOON_DAYS = 3;

/**
 * Graceful in-chat response for expired trials (owner directive 2026-07-31):
 * the user must not be able to keep chatting, and the reply should point them
 * at self-serve activation instead of a top-up CTA.
 */
export const TRIAL_ENDED_CHAT_MESSAGE =
  `🔒 your trial has ended — thanks for exploring Prest0n! chat is paused for now, but your workspace and everything Prest0n has learned are safe. hire your Prest0n on the subscription page and everything picks up right where it left off.`;

export interface TrialDocShape {
  active?: boolean | null;
  tier?: 'a' | 'b' | string | null;
  creditAmountCents?: number | null;
  creditRemainingCents?: number | null;
  expiresAt?: TrialExpiryValue;
}

/**
 * Minimal shape of users/{uid}/subscription/subscription for trial gating.
 * A user counts as a trial user ONLY when their CURRENT subscription says so
 * (written by redeemAccessCode): is_trial === true or plan === 'trial'.
 *
 * This is the load-bearing gate: early activated accounts (e.g. Fernando's
 * enterprise tenant) still carry a legacy users/{uid}.trial field with a
 * long-past expiry — keying trial UI off that field alone showed them a
 * "your trial has ended" banner. Their subscription doc says enterprise/
 * active, so the gate excludes them; converting a trial to a full plan
 * rewrites the subscription doc and clears the trial UI the same way.
 */
export interface TrialSubscriptionShape {
  is_trial?: boolean | null;
  plan?: string | null;
  status?: string | null;
  is_client_free?: boolean | null;
  trial_credit_cents?: number | null;
  trial_expires_at?: TrialExpiryValue;
  /** Server-mirrored exempt-organization flag (task #76) — display only. */
  trial_exempt?: boolean | null;
}

export function isTrialSubscription(sub: TrialSubscriptionShape | null | undefined): boolean {
  // Client-free accounts (Prest0 absorbs cost — internal/testing/comped) are
  // never trial-gated, even if a stray trial flag exists on the doc.
  if (sub?.is_client_free === true) return false;
  return sub?.is_trial === true || sub?.plan === 'trial';
}

export interface TrialStatus {
  /** True until the first snapshot has arrived (or auth resolves signed-out). */
  isLoading: boolean;
  /** True if this user ever redeemed a trial (the trial field exists). */
  isTrialUser: boolean;
  /** True while the trial is active and not past its end date. */
  isActive: boolean;
  /** True once the trial is over (flagged inactive server-side or past end date). */
  isExpired: boolean;
  /** True when active but ending within TRIAL_EXPIRING_SOON_DAYS days. */
  isExpiringSoon: boolean;
  tier: 'a' | 'b' | null;
  creditAmountCents: number;
  creditRemainingCents: number;
  /** Whole days until the end date (ceil), 0 once past. Null if unknown. */
  daysRemaining: number | null;
  /** Friendly end date, e.g. "August 28". Null if unknown. */
  endsOn: string | null;
  expiresAtMs: number | null;
  /**
   * The workspace's LIVE wallet balance in pr0 (users/{uid}/wallet/summary
   * olv_balance) — the one number every trial surface displays, so the
   * banner always lines up with the wallet page (task #76, 2026-08-18
   * owner directive). Null until the wallet snapshot lands.
   */
  liveWalletWu: number | null;
  /** Exempt organization (task #76): hide test-mode chrome, never blocked. */
  trialExempt: boolean;
}

export const NO_TRIAL: TrialStatus = {
  isLoading: false,
  isTrialUser: false,
  isActive: false,
  isExpired: false,
  isExpiringSoon: false,
  tier: null,
  creditAmountCents: 0,
  creditRemainingCents: 0,
  daysRemaining: null,
  endsOn: null,
  expiresAtMs: null,
  liveWalletWu: null,
  trialExempt: false,
};

/**
 * WORKSPACE trial facts as stamped on accounts/{id}/wallet/displaySummary by
 * ensureAccountWalletDisplayMirror (functions/src/lib/accountExpense.ts) —
 * the one member-readable doc the wallet page already trusts for the acting
 * account's balance (owner report 2026-08-17: the trial banner must reflect
 * the ACTIVE workspace's numbers, exactly like the wallet page).
 */
export interface WorkspaceTrialFacts {
  /** users/{ownerUid}/subscription/subscription exists for the workspace owner. */
  subscriptionDocExists: boolean;
  subscriptionPlan: string | null;
  subscriptionIsTrial: boolean;
  subscriptionIsClientFree: boolean;
  trialActive: boolean | null;
  trialTier: string | null;
  /** Redemption-time credit SEED in cents (never decrements). */
  trialCreditCents: number | null;
  trialExpiresAtMs: number | null;
}

/**
 * Parse the trial-facts fields off a raw displaySummary doc. Returns null for
 * mirrors that predate the trial-facts stamp (no subscription_doc_exists /
 * trial_synced_at marker) — callers fail toward "no trial banner" until the
 * next ensure refresh stamps them.
 */
export function parseWorkspaceTrialFacts(
  data: Record<string, unknown> | null | undefined,
): WorkspaceTrialFacts | null {
  if (!data || typeof data !== 'object') return null;
  if (!('subscription_doc_exists' in data) && !('trial_synced_at' in data)) return null;
  const num = (v: unknown): number | null =>
    typeof v === 'number' && isFinite(v) ? v : null;
  return {
    subscriptionDocExists: data.subscription_doc_exists === true,
    subscriptionPlan: typeof data.subscription_plan === 'string' ? data.subscription_plan : null,
    subscriptionIsTrial: data.subscription_is_trial === true,
    subscriptionIsClientFree: data.subscription_is_client_free === true,
    trialActive: typeof data.trial_active === 'boolean' ? data.trial_active : null,
    trialTier: typeof data.trial_tier === 'string' ? data.trial_tier : null,
    trialCreditCents: num(data.trial_credit_cents),
    trialExpiresAtMs: num(data.trial_expires_at_ms),
  };
}

/**
 * Derive the ACTIVE WORKSPACE's trial status from the display-mirror facts +
 * the mirror's live paying-wallet balance (converted to cents by the caller
 * at the same rate the wallet page uses). Same gating/derivation rules as
 * deriveTrialStatus — including the Fernando-class subscription gate and the
 * narrow trial-only-on-user-doc exception — so the banner can never disagree
 * with what the workspace owner's own surfaces would say.
 *
 * `facts === null` (mirror missing, unreadable, or predating the trial-facts
 * stamp) fails toward NO_TRIAL: in a foreign workspace the member's native
 * trial state must never leak into the banner, and a hidden banner is the
 * safe direction until the next workspace switch / org-facts bridge refresh
 * stamps the facts.
 */
export function deriveWorkspaceTrialStatus(
  facts: WorkspaceTrialFacts | null,
  liveBalanceCents: number | null,
  nowMs: number,
  liveWalletWu: number | null = null,
): TrialStatus {
  if (!facts) return { ...NO_TRIAL };
  const subscription: TrialSubscriptionShape | null = facts.subscriptionDocExists
    ? {
        is_trial: facts.subscriptionIsTrial === true,
        plan: facts.subscriptionPlan,
        is_client_free: facts.subscriptionIsClientFree === true,
      }
    : null;
  const hasTrialField =
    facts.trialActive !== null || facts.trialExpiresAtMs !== null || facts.trialCreditCents !== null;
  const trial: TrialDocShape | null = hasTrialField
    ? {
        active: facts.trialActive,
        tier: facts.trialTier,
        creditAmountCents: facts.trialCreditCents,
        // The seed is the static fallback; the live paying-wallet balance
        // overrides below (same policy as the native hook's wallet listener).
        creditRemainingCents: facts.trialCreditCents,
        expiresAt: facts.trialExpiresAtMs,
      }
    : null;
  const status = deriveTrialStatus(trial, subscription, nowMs);
  if (status.isTrialUser && liveBalanceCents !== null) {
    // ONE number everywhere (task #76, 2026-08-18 owner directive): trial
    // surfaces print the workspace's ACTUAL wallet pr0 — identical to the
    // wallet page — never a seed-clamped figure that drifts from it.
    return {
      ...status,
      creditRemainingCents: Math.max(0, Math.round(liveBalanceCents)),
      liveWalletWu: liveWalletWu !== null ? Math.max(0, liveWalletWu) : status.liveWalletWu,
    };
  }
  return {
    ...status,
    liveWalletWu: liveWalletWu !== null ? Math.max(0, liveWalletWu) : status.liveWalletWu,
  };
}

export function deriveTrialStatus(
  trial: TrialDocShape | null,
  subscription: TrialSubscriptionShape | null,
  nowMs: number,
): TrialStatus {
  // Gate on the CURRENT subscription — the legacy users/{uid}.trial field
  // alone must never mark someone as a trial user (see TrialSubscriptionShape).
  //
  // One narrow exception (2026-08-15 owner report: a trial account saw no
  // trial banner at all). An account whose trial lives ONLY in the user doc —
  // no subscription document exists yet — is genuinely on a trial and had no
  // status surface anywhere. The exception is deliberately narrow so it cannot
  // reopen the incident the gate was built for:
  //   · it requires the subscription doc to be ABSENT, and every activated
  //     account has one (that is what excluded the enterprise tenants), and
  //   · it requires the trial to be active AND not past its end date, so a
  //     stale legacy trial field can never resurrect trial UI.
  if (!isTrialSubscription(subscription)) {
    const trialOnlyOnUserDoc =
      subscription == null &&
      trial?.active === true &&
      (() => {
        const ms = expiryToMillis(trial?.expiresAt);
        return ms !== null && ms > nowMs;
      })();
    if (!trialOnlyOnUserDoc) {
      return { ...NO_TRIAL };
    }
  }

  // Trial subscription without the user-doc trial field (edge: doc read raced
  // redemption) — derive what we can from the subscription itself.
  if (!trial) {
    trial = {
      active: true,
      creditAmountCents: subscription?.trial_credit_cents ?? null,
      creditRemainingCents: subscription?.trial_credit_cents ?? null,
      expiresAt: subscription?.trial_expires_at,
    };
  }

  const expiresAtMs = expiryToMillis(trial.expiresAt);
  const pastEnd = expiresAtMs !== null && expiresAtMs <= nowMs;
  const isActive = trial.active === true && !pastEnd;
  const isExpired = trial.active === false || pastEnd;
  const daysRemaining = expiresAtMs !== null
    ? Math.max(0, Math.ceil((expiresAtMs - nowMs) / (1000 * 60 * 60 * 24)))
    : null;
  const endsOn = expiresAtMs !== null
    ? new Date(expiresAtMs).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;

  return {
    isLoading: false,
    isTrialUser: true,
    isActive,
    isExpired,
    isExpiringSoon: isActive && daysRemaining !== null && daysRemaining <= TRIAL_EXPIRING_SOON_DAYS,
    tier: trial.tier === 'a' || trial.tier === 'b' ? trial.tier : null,
    creditAmountCents: typeof trial.creditAmountCents === 'number' ? trial.creditAmountCents : 0,
    creditRemainingCents: typeof trial.creditRemainingCents === 'number' ? trial.creditRemainingCents : 0,
    daysRemaining,
    endsOn,
    expiresAtMs,
    liveWalletWu: null,
    trialExempt: subscription?.trial_exempt === true,
  };
}
